import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import MessageSubmitForm from "@/components/MessageSubmitForm";
import MessageFeed from "@/components/MessageFeed";
import SpeaksStatsBar from "@/components/SpeaksStatsBar";
import ShareLine from "@/components/ShareLine";
import PollHeroSlider from "@/components/PollHeroSlider";
import SeoHead from "@/components/SeoHead";
import { useLang } from "@/lib/useLang";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Home = () => {
  const { t, lang } = useLang();
  const [refreshKey, setRefreshKey] = useState(0);
  const [topMessages, setTopMessages] = useState([]);

  const handleSubmitted = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  // Pull top-10 for ItemList structured data (helps Google/AEO understand the page).
  useEffect(() => {
    axios.get(`${API}/messages`, { params: { sort: "upvotes", limit: 10, skip: 0 } })
      .then((r) => setTopMessages(r.data?.messages || []))
      .catch(() => {});
  }, [refreshKey]);

  const homeSchema = [
    {
      "@type": "WebPage",
      "@id": "https://thenextcm.com/#kerala-speaks-home",
      "url": "https://thenextcm.com/",
      "name": "Kerala Speaks — what should the new CM focus on first?",
      "isPartOf": { "@id": "https://thenextcm.com/#website" },
      "about": {
        "@type": "Thing",
        "name": "First 100 days of Kerala's new Chief Minister",
      },
      "audience": { "@type": "Audience", "audienceType": "Citizens of Kerala" },
      "inLanguage": ["en", "ml"],
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is Kerala Speaks?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Kerala Speaks is an independent, non-partisan civic platform where Keralites send short public messages to the new Chief Minister. It is the successor to the citizens' poll at thenextcm.com that gathered over half a million votes choosing Kerala's next CM."
          }
        },
        {
          "@type": "Question",
          "name": "Who runs Kerala Speaks?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Kerala Speaks is run independently of any political party. There are no donors, no political backers, and no editorial line beyond civic, civil, and clear. Contact: keralaspeaks7@gmail.com."
          }
        },
        {
          "@type": "Question",
          "name": "How are messages moderated?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Every submission passes a hybrid moderation pipeline: a word-list profanity filter, duplicate detection, IP rate limiting, and an AI moderator (Claude Haiku 4.5) that rejects abuse, hate speech, slurs, and personal attacks while allowing strong political criticism."
          }
        },
        {
          "@type": "Question",
          "name": "Can I post in Malayalam?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. The entire platform supports English and Malayalam (മലയാളം). Messages can be up to 500 characters, Unicode-safe so Malayalam conjuncts count correctly."
          }
        },
        {
          "@type": "Question",
          "name": "Is Kerala Speaks an official government channel?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No. Kerala Speaks is a citizens' platform, not an official channel of the Government of Kerala, the Chief Minister's office, or any political party. Messages are public civic input, not formal petitions."
          }
        }
      ]
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thenextcm.com/" }
      ]
    },
    ...(topMessages.length > 0 ? [{
      "@type": "ItemList",
      "@id": "https://thenextcm.com/#top-messages",
      "name": "Top citizen messages — Kerala Speaks",
      "numberOfItems": topMessages.length,
      "itemListOrder": "https://schema.org/ItemListOrderDescending",
      "itemListElement": topMessages.map((m, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "url": `https://thenextcm.com/m/${m.id}`,
        "name": (m.message || "").slice(0, 110),
      })),
    }] : []),
  ];

  return (
    <div className="App min-h-screen">
      <SeoHead
        title={lang === "ml"
          ? "കേരളം പറയുന്നു — കേരളം ആദ്യം എന്തിൽ ശ്രദ്ധിക്കണം?"
          : "Kerala Speaks — What Should Kerala Focus On First?"}
        description={lang === "ml"
          ? "അഞ്ച് ലക്ഷം മലയാളികൾ കേരളത്തിന്റെ പുതിയ നേതാവിനെ തിരഞ്ഞെടുത്തു. ഇപ്പോൾ കേരളം പറയുന്നു — അടുത്തത് എന്താണ്. ആദ്യ 100 ദിവസങ്ങൾക്കായി നിങ്ങളുടെ സന്ദേശം പങ്കിടുക."
          : "Half a million citizens chose Kerala's next leader. Now Kerala speaks about what comes next. Share your message for the first 100 days."}
        canonical="/"
        schema={homeSchema}
      />
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 pt-10 md:pt-16 pb-6" data-testid="home-hero">
        <p
          className="text-xs md:text-sm uppercase font-bold tracking-[0.4em] text-neutral-600 mb-5"
          data-testid="home-eyebrow"
        >
          ★ {t("home_eyebrow")} ★
        </p>

        {/* Multi-poll banner — sits between the brand eyebrow and the main
            headline so visitors immediately see live civic polls without
            scrolling past the static hero. */}
        <div className="mb-8 md:mb-10">
          <PollHeroSlider />
        </div>

        <h1
          className="font-display uppercase leading-[0.85] tracking-tighter"
          style={{ fontSize: "clamp(3rem, 10vw, 8rem)" }}
          data-testid="home-title"
        >
          {t("home_title_1")}<br />
          <span style={{ color: "#E3000F" }}>{t("home_title_2")}</span>
        </h1>
        <p
          className="mt-6 max-w-3xl text-base md:text-xl font-medium leading-relaxed"
          data-testid="home-sub"
        >
          {t("home_sub")}
        </p>
      </section>

      <main className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 pb-20 space-y-10 md:space-y-12">
        <MessageSubmitForm onSubmitted={handleSubmitted} />

        {/* Stats sit directly above the "What Kerala is saying" feed so the
            counts (messages, upvotes, top category) are clearly understood
            as belonging to the open conversation below, not to the multi-poll
            hero above it. */}
        <SpeaksStatsBar refreshKey={refreshKey} />

        <MessageFeed refreshKey={refreshKey} />

        <ShareLine />

        {/* Previous poll CTA */}
        <section
          className="brutal-card p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-5"
          style={{ background: "#0A0A0A", color: "#FDFBF7" }}
          data-testid="previous-poll-cta"
        >
          <div className="max-w-2xl">
            <p
              className="text-xs md:text-sm uppercase font-bold tracking-[0.4em] mb-3"
              style={{ color: "#FF9933" }}
            >
              ★ {t("previous_poll_cta_eyebrow")} ★
            </p>
            <h2
              className="font-display uppercase leading-none"
              style={{ fontSize: "clamp(1.5rem, 3.5vw, 2.25rem)", color: "#FDFBF7" }}
            >
              {t("previous_poll_cta_title")}
            </h2>
            <p className="text-sm md:text-base font-medium mt-2 opacity-90">
              {t("previous_poll_cta_sub")}
            </p>
          </div>
          <Link
            to="/previous-polls/kerala-cm-2026"
            className="brutal-btn shrink-0"
            style={{ background: "#FF9933", color: "#0A0A0A", borderColor: "#FF9933" }}
            data-testid="previous-poll-cta-btn"
          >
            {t("previous_poll_cta_button")} →
          </Link>
        </section>
      </main>
    </div>
  );
};

export default Home;
